/**
 * Pieces every contract is built from.
 *
 * The rule that shapes all of these: **the API carries ids, never rendered
 * text** (Q12.2). A code name is `codeNameId`, an island name is the structure
 * the day drew, a log line is `{ event, seed }`, a relic and a fragment are
 * catalogue ids. The reader's client renders every one of them through the
 * core loaders in the reader's locale, which is also how two players who do
 * not share a language can correspond.
 *
 * The one exception is the share card, which is a block of text by definition
 * and is rendered before it is served.
 */

import { z } from 'zod'
import { GAME_CONFIG, REPUTATION_TIERS } from '../game-config.ts'
import { LOG_EVENTS } from '../content/schemas.ts'
import { BUDGETS } from '../movement.ts'
import { CARDINALS, DIRECTIONS } from '../tiles.ts'

const { grid } = GAME_CONFIG

/** A UTC game day. Days are strings everywhere; never a Date (`AGENTS.md`). */
export const gameDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/** A tile on the island grid. */
export const positionSchema = z.object({
  x: z
    .number()
    .int()
    .min(0)
    .max(grid.width - 1),
  y: z
    .number()
    .int()
    .min(0)
    .max(grid.height - 1),
})
export type ApiPosition = z.infer<typeof positionSchema>

/** A direction a client may ask to move in. Diagonals need an ability (Q2.2). */
export const directionSchema = z.enum(
  Object.keys(DIRECTIONS) as [keyof typeof DIRECTIONS, ...Array<keyof typeof DIRECTIONS>],
)
export const cardinalSchema = z.enum(
  Object.keys(CARDINALS) as [keyof typeof CARDINALS, ...Array<keyof typeof CARDINALS>],
)

/** The only thing anyone ever learns about a reputation score (Q5.2). */
export const reputationTierSchema = z.enum(REPUTATION_TIERS)

export const budgetSchema = z.enum(BUDGETS)

/**
 * A log line, as an event and a seed. The client shows
 * `lines[seed % lines.length]` from its own locale, so locales may hold
 * different numbers of lines and the pick stays deterministic per move.
 */
export const logLineSchema = z.object({
  event: z.enum(LOG_EVENTS),
  seed: z.number().int().nonnegative(),
})
export type ApiLogLine = z.infer<typeof logLineSchema>

/** A generated island name, as the day row stores it (Q12.4). */
export const islandNameSchema = z.object({
  pattern: z.string(),
  slots: z.record(z.string(), z.number().int().nonnegative()),
})

/** Where an island is in its two-day life (`docs/rules.md` section 1). */
export const dayStatusSchema = z.enum(['rising', 'sinking', 'finalised'])

/** How a tile came to be on a player's map. */
export const tileSourceSchema = z.enum(['seen', 'hearsay'])

/**
 * A tile a player may see: their own sight, or somebody's claim. Hearsay
 * carries the delivery that claimed it -- an inbox public id, never a sender.
 */
export const mapCellSchema = z.object({
  x: positionSchema.shape.x,
  y: positionSchema.shape.y,
  glyph: z.string(),
  source: tileSourceSchema,
  /** The inbox `public_id` that claimed a hearsay tile. */
  from: z.string().optional(),
})
export type ApiMapCell = z.infer<typeof mapCellSchema>

/** Every failure the API reports, as a code the client renders itself. */
export const apiErrorSchema = z.object({
  error: z.string(),
  /** A log event to show instead of a generic message, where one fits. */
  log: logLineSchema.optional(),
})
export type ApiError = z.infer<typeof apiErrorSchema>

export const okSchema = z.object({ ok: z.literal(true) })
